import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { Search, X } from "lucide-react";

const SVG_NS = "http://www.w3.org/2000/svg";

const PIECE_COLORS = [
  { fill: "#3b82f6", stroke: "#1e40af" },
  { fill: "#22c55e", stroke: "#15803d" },
  { fill: "#f97316", stroke: "#c2410c" },
  { fill: "#8b5cf6", stroke: "#5b21b6" },
  { fill: "#ec4899", stroke: "#be185d" },
  { fill: "#eab308", stroke: "#a16207" },
  { fill: "#06b6d4", stroke: "#0e7490" },
];

export default function NestingDiagram({ placements, rollWidth, rollLength, materialType, totalSheets, fallbackShape }) {
  const svgRef = useRef(null);
  const expandedSvgRef = useRef(null);

  const numSheets = Math.max(totalSheets || 1, 1);
  const isSampled = false;

  const [currentSheet, setCurrentSheet] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => { setCurrentSheet(0); setExpanded(false); }, [placements]);

  const sheetPlacements = useMemo(() => {
    if (!placements) return [];
    if (numSheets <= 1) return placements;
    return placements.filter(p => (p.sheet ?? 0) === currentSheet);
  }, [placements, currentSheet, numSheets]);

  const diagramData = useMemo(() => {
    if (!sheetPlacements || sheetPlacements.length === 0 || !rollWidth) return null;

    const effectiveLength = rollLength || rollWidth;
    const maxVisibleLength = Math.min(effectiveLength, rollWidth * 3);
    const visiblePlacements = sheetPlacements.filter(p =>
      p.w > 0 && p.h > 0 && (p.y + p.h <= maxVisibleLength + 10)
    );
    if (visiblePlacements.length === 0) return null;

    let maxX = 0, maxY = 0;
    visiblePlacements.forEach(p => {
      maxX = Math.max(maxX, p.x + p.w);
      maxY = Math.max(maxY, p.y + p.h);
    });

    const extentX = Math.max(rollWidth, maxX);
    const extentY = Math.max(maxY + 10, effectiveLength);

    return { visiblePlacements, extentX, extentY };
  }, [sheetPlacements, rollWidth, rollLength]);

  const drawToSvg = useCallback((svg) => {
    if (!svg || !diagramData) return;

    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const { visiblePlacements, extentX, extentY } = diagramData;
    const padding = 32;
    const svgWidth = 640;
    const svgHeight = 360;
    const drawWidth = svgWidth - padding * 2;
    const drawHeight = svgHeight - padding * 2;
    const scale = Math.min(drawWidth / extentX, drawHeight / extentY);
    const rollW = extentX * scale;
    const rollH = extentY * scale;
    const dispLength = rollLength || extentY;

    const el = (tag, attrs) => {
      const e = document.createElementNS(SVG_NS, tag);
      for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
      return e;
    };

    svg.appendChild(el("rect", {
      x: padding, y: padding, width: rollW, height: rollH,
      fill: "#fafaf9", stroke: "#a8a29e", "stroke-width": "1", "stroke-dasharray": "6 3"
    }));

    svg.appendChild(el("line", { x1: padding, y1: padding - 12, x2: padding + rollW, y2: padding - 12, stroke: "#78716c", "stroke-width": "0.7" }));
    svg.appendChild(el("line", { x1: padding, y1: padding - 16, x2: padding, y2: padding - 8, stroke: "#78716c", "stroke-width": "0.7" }));
    svg.appendChild(el("line", { x1: padding + rollW, y1: padding - 16, x2: padding + rollW, y2: padding - 8, stroke: "#78716c", "stroke-width": "0.7" }));
    const widthLabel = el("text", {
      x: padding + rollW / 2, y: padding - 16,
      "text-anchor": "middle", "font-size": "9", fill: "#44403c", "font-family": "monospace", "font-weight": "600"
    });
    widthLabel.textContent = `${rollWidth.toLocaleString()} mm`;
    svg.appendChild(widthLabel);

    svg.appendChild(el("line", { x1: padding - 12, y1: padding, x2: padding - 12, y2: padding + rollH, stroke: "#78716c", "stroke-width": "0.7" }));
    svg.appendChild(el("line", { x1: padding - 16, y1: padding, x2: padding - 8, y2: padding, stroke: "#78716c", "stroke-width": "0.7" }));
    svg.appendChild(el("line", { x1: padding - 16, y1: padding + rollH, x2: padding - 8, y2: padding + rollH, stroke: "#78716c", "stroke-width": "0.7" }));
    const lenLabel = el("text", {
      x: padding - 18, y: padding + rollH / 2,
      "text-anchor": "middle", "font-size": "9", fill: "#44403c", "font-family": "monospace", "font-weight": "600",
      transform: `rotate(-90, ${padding - 18}, ${padding + rollH / 2})`
    });
    lenLabel.textContent = `${dispLength.toFixed(0)} mm`;
    svg.appendChild(lenLabel);

    visiblePlacements.forEach((p, i) => {
      const c = PIECE_COLORS[i % PIECE_COLORS.length];
      const rx = padding + p.x * scale;
      const ry = padding + p.y * scale;
      const rw = p.w * scale;
      const rh = p.h * scale;

      const shape = (p.shape_points && p.shape_points.length >= 3) ? p.shape_points : fallbackShape;
      if (shape && shape.length >= 3) {
        svg.appendChild(el("rect", {
          x: rx, y: ry, width: rw, height: rh,
          fill: c.fill, "fill-opacity": "0.12", stroke: c.stroke, "stroke-width": "0.5",
          "stroke-dasharray": "3 2", rx: "1"
        }));
        const transformedPts = p.rotated
          ? shape.map(([nx, ny]) => [1 - ny, nx])
          : shape;
        const pts = transformedPts.map(([nx, ny]) =>
          `${rx + nx * rw},${ry + ny * rh}`
        ).join(" ");
        svg.appendChild(el("polygon", {
          points: pts,
          fill: c.fill, "fill-opacity": "0.85", stroke: c.stroke, "stroke-width": "1.8",
          "stroke-linejoin": "round"
        }));
      } else {
        svg.appendChild(el("rect", {
          x: rx, y: ry, width: rw, height: rh,
          fill: c.fill, "fill-opacity": "0.85", stroke: c.stroke, "stroke-width": "1.8", rx: "2"
        }));
      }
    });

    visiblePlacements.forEach((p, i) => {
      const c = PIECE_COLORS[i % PIECE_COLORS.length];
      const rx = padding + p.x * scale;
      const ry = padding + p.y * scale;
      const rw = p.w * scale;
      const rh = p.h * scale;
      const showDims = rw > 30 && rh > 16;
      const showLabel = rw > 42 && rh > 24;

      if (showLabel) {
        const label = el("text", {
          x: rx + rw / 2, y: ry + rh / 2 - (showDims ? 5 : 0),
          "text-anchor": "middle", "dominant-baseline": "central",
          "font-size": Math.min(12, rw / 5), fill: c.stroke, "font-weight": "700", "font-family": "sans-serif"
        });
        label.textContent = `${i + 1}`;
        svg.appendChild(label);
      }

      if (showDims) {
        const dims = el("text", {
          x: rx + rw / 2, y: ry + rh / 2 + (showLabel ? 8 : 0),
          "text-anchor": "middle", "dominant-baseline": "central",
          "font-size": "7.5", fill: "#555", "font-family": "monospace", "font-weight": "600"
        });
        dims.textContent = `${p.w.toFixed(1)} x ${p.h.toFixed(1)} mm`;
        svg.appendChild(dims);
      }

      if (!showLabel) {
        const idx = el("text", {
          x: rx + rw / 2, y: ry + rh / 2,
          "text-anchor": "middle", "dominant-baseline": "central",
          "font-size": "7", fill: c.stroke, "font-weight": "700", "font-family": "monospace"
        });
        idx.textContent = `${i + 1}`;
        svg.appendChild(idx);
      }
    });

    const sheetLabel = numSheets > 1 ? `Sheet ${currentSheet + 1}/${numSheets} | ` : "";
    const sampledLabel = isSampled ? ` | sample (${visiblePlacements.length} of ${totalSheets * Math.round(visiblePlacements.length / numSheets)} pcs shown)` : "";
    svg.appendChild(el("rect", {
      x: padding, y: svgHeight - 20, width: rollW, height: 18, fill: "#1c1917", rx: "2"
    }));
    const summary = el("text", {
      x: padding + 8, y: svgHeight - 8,
      "font-size": "8", fill: "#e7e5e4", "font-family": "monospace", "font-weight": "500"
    });
    summary.textContent = `${sheetLabel}${materialType === "SHEET" ? "SHEET" : "ROLL"} | ${visiblePlacements.length} piece(s) | ${rollWidth.toLocaleString()} x ${dispLength.toFixed(0)} mm${sampledLabel}`;
    svg.appendChild(summary);
  }, [diagramData, rollWidth, rollLength, materialType, currentSheet, numSheets, isSampled, totalSheets]);

  useEffect(() => { drawToSvg(svgRef.current); }, [drawToSvg]);
  useEffect(() => { if (expanded) drawToSvg(expandedSvgRef.current); }, [drawToSvg, expanded]);

  const nav = numSheets > 1 && (
    <div className="flex items-center justify-center gap-3 mt-2 select-none">
      <button
        onClick={() => setCurrentSheet(s => Math.max(0, s - 1))}
        disabled={currentSheet === 0}
        className="w-8 h-8 flex items-center justify-center border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-lg leading-none"
      >‹</button>
      <span className="text-xs font-mono text-slate-600 font-semibold tracking-wider">
        Sheet {currentSheet + 1} / {numSheets}
        {sheetPlacements.length > 0 && <span className="text-slate-400 font-normal ml-1">({sheetPlacements.length} pcs)</span>}
      </span>
      <button
        onClick={() => setCurrentSheet(s => Math.min(numSheets - 1, s + 1))}
        disabled={currentSheet === numSheets - 1}
        className="w-8 h-8 flex items-center justify-center border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-lg leading-none"
      >›</button>
    </div>
  );

  if (!diagramData) {
    return (
      <div className="w-full" data-testid="nesting-diagram">
        <div className="flex items-center justify-center h-32 text-slate-400 text-sm bg-slate-50 border border-dashed border-slate-300">
          {numSheets > 1 ? `No pieces on Sheet ${currentSheet + 1}` : "No nesting visualization available"}
        </div>
        {nav}
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="nesting-diagram">
      <div className="relative">
        <svg
          ref={svgRef}
          viewBox="0 0 640 360"
          className="w-full bg-white"
          preserveAspectRatio="xMidYMid meet"
        />
        <button
          onClick={() => setExpanded(true)}
          className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-white/90 border border-slate-300 text-slate-600 hover:bg-slate-100 hover:text-slate-900 shadow-sm"
          title="View fullscreen"
        >
          <Search size={15} />
        </button>
      </div>
      {nav}

      {/* Fullscreen modal */}
      {expanded && (
        <div
          className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4"
          onClick={() => setExpanded(false)}
        >
          <div
            className="bg-white w-full max-w-5xl shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-900 text-white">
              <span className="text-xs font-semibold uppercase tracking-wider">Nesting Layout</span>
              <button
                onClick={() => setExpanded(false)}
                className="w-7 h-7 flex items-center justify-center hover:bg-slate-700 text-slate-300 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              <svg
                ref={expandedSvgRef}
                viewBox="0 0 640 360"
                className="w-full bg-white"
                preserveAspectRatio="xMidYMid meet"
              />
              {nav}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
